import {acceptedFileTypes, useUploadFile} from '@github-ui/comment-box/fileUpload'
import type {RepoSubject} from '@github-ui/comment-box/subject'
import type {FileUploadResult} from '@github-ui/markdown-editor'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useCallback, useImperativeHandle, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {prefixMarkdownTitle} from '../utils/markdown-conversion'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import type {UploadElement_input$data, UploadElement_input$key} from './__generated__/UploadElement_input.graphql'
import {ElementWrapper} from './ElementWrapper'

export type UploadElementInternalProps = GraphqlFragment<UploadElement_input$data> & {
  type: 'upload'
  index?: number
  ref?: React.Ref<IssueFormElementRef>
  subject: RepoSubject
} & SharedElementProps

type UploadElementProps = {
  elementRef: UploadElement_input$key
  index?: number
  ref?: React.Ref<IssueFormElementRef>
  subject: RepoSubject
} & SharedElementProps

export const UploadElement = ({ref, elementRef, sessionStorageKey, ...props}: UploadElementProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment UploadElement_input on IssueFormElementUpload {
        __id
        itemId: id
        label
        descriptionHTML
        required
      }
    `,
    elementRef,
  )

  const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', data.label, data.__id)
  return <UploadElementInternal {...data} {...props} type={'upload'} sessionStorageKey={elementStorageKey} ref={ref} />
}

export const UploadElementInternal = ({
  ref,
  label,
  descriptionHTML,
  required,
  itemId,
  subject,
  index: indexOfFormElement,
  sessionStorageKey,
  onFieldChange,
}: UploadElementInternalProps) => {
  const [textValue, setTextValue] = useSessionStorage<string>(sessionStorageKey, '')
  const [uploadResults, setUploadResults] = useState<FileUploadResult[]>([])

  const inputRef = useRef<HTMLInputElement>(null)
  const onUploadFile = useUploadFile(subject.repository.databaseId.toString())
  const handleValueChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files
      if (!selectedFiles) {
        return
      }

      const uploads: FileUploadResult[] = []
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i]
        if (file) {
          try {
            const result = await onUploadFile(file)
            uploads.push(result)
          } catch {
            // Swallow individual upload errors so a single failure
            // does not reject the entire change handler.
          }
        }
      }

      setUploadResults(prevUploadResults => [...prevUploadResults, ...uploads])

      const markdownLinks = uploads
        .map(result => {
          if (result.url) {
            return `[${result.file.name}](${result.url})`
          }
          return ''
        })
        .join('\n')

      setTextValue(prevTextValue => {
        const newTextValue = prevTextValue ? `${prevTextValue}\n${markdownLinks}` : markdownLinks
        onFieldChange?.(itemId, indexOfFormElement, newTextValue)
        return newTextValue
      })
    },
    [setTextValue, onUploadFile, onFieldChange, itemId, indexOfFormElement],
  )

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      markdown: () => prefixMarkdownTitle(label, textValue),
      validate: () => {
        if (!required) {
          return true
        }

        const hasUploads = uploadResults.length > 0
        const hasText = textValue.trim() !== ''

        return hasUploads || hasText
      },
      reset: () => {
        setTextValue('')
        setUploadResults([])
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      },
      hasChanges: () => textValue !== '',
      getSessionStorageKey: () => sessionStorageKey,
      type: 'upload' as const,
    }),
    [label, required, sessionStorageKey, textValue, uploadResults, setTextValue, setUploadResults],
  )

  return (
    <ElementWrapper label={label} description={descriptionHTML} required={required}>
      {({labelId, descriptionIds}) => (
        <>
          {uploadResults.length > 0 && (
            <ul>
              {uploadResults.map((result, index) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <li key={index}>
                  {result.url ? (
                    <a href={result.url} target="_blank" rel="noreferrer">
                      {result.file.name}
                    </a>
                  ) : (
                    result.file.name
                  )}
                </li>
              ))}
            </ul>
          )}
          <input
            type="file"
            ref={inputRef}
            aria-labelledby={labelId}
            aria-describedby={descriptionIds}
            onChange={handleValueChange}
            accept={acceptedFileTypes(true).join(', ')}
            multiple
          />
        </>
      )}
    </ElementWrapper>
  )
}
